/**
 * SELF-PROTECTING API CONFIGURATION
 * ==================================
 * 
 * This module ensures API calls NEVER use incorrect base URLs in production.
 * 
 * Rules:
 * 1. Production ALWAYS uses same-origin ("/api")
 * 2. Preview/external URLs are BLOCKED in production
 * 3. Auto-fallback to safe defaults if misconfigured
 * 4. Single source of truth - no component should define its own API URL
 */

// Forbidden patterns that should NEVER appear in production API URLs
const FORBIDDEN_PATTERNS = [
  'emergent',
  'preview',
  'cluster',
  'localhost',
  'ngrok',
  'tunnel',
  '.dev',
  '.test',
  '.local'
];

import { trackLeadConversion } from '../lib/adsConversion';


// Detect environment
const isProduction = () => {
  // Check multiple indicators of production
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const nodeEnv = process.env.NODE_ENV;
  
  // Production indicators
  const productionHosts = [
    'moveabroadiq.com',
    'www.moveabroadiq.com'
  ];
  
  return nodeEnv === 'production' || productionHosts.some(h => hostname.includes(h));
};

// Check if URL contains forbidden patterns
const containsForbiddenPattern = (url) => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return FORBIDDEN_PATTERNS.some(pattern => lowerUrl.includes(pattern));
};

// Validate and sanitize API base URL
const getSecureApiBase = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  const isProd = isProduction();
  
  // RULE 1: Production ALWAYS uses same-origin
  if (isProd) {
    if (envUrl && containsForbiddenPattern(envUrl)) {
      console.error('[API CONFIG] BLOCKED: Attempted to use forbidden URL in production:', envUrl);
      console.warn('[API CONFIG] Auto-fallback to same-origin /api');
    }
    return ''; // Empty string = same-origin, will use /api/...
  }
  
  // RULE 2: Development/Preview - allow env URL but validate
  if (envUrl) {
    // Additional safety: even in dev, warn about suspicious URLs
    if (containsForbiddenPattern(envUrl) && !envUrl.includes('preview.emergentagent.com')) {
      console.warn('[API CONFIG] Warning: Using potentially unsafe API URL:', envUrl);
    }
    return envUrl;
  }
  
  // RULE 3: Fallback to same-origin
  return '';
};

// The protected API base URL
const API_BASE = getSecureApiBase();

// Log API mode on initialization
const logApiMode = () => {
  const isProd = isProduction();
  const mode = isProd ? 'PRODUCTION' : 'DEVELOPMENT';
  const target = API_BASE || '(same-origin)';
  
  console.log(`[API CONFIG] Mode: ${mode}`);
  console.log(`[API CONFIG] Base URL: ${target}`);
  
  if (isProd) {
    console.log('[API CONFIG] Production mode: All API calls use same-origin /api');
  }
};

// Initialize logging
if (typeof window !== 'undefined') {
  logApiMode();
}

/**
 * Build full API URL with protection
 * @param {string} endpoint - API endpoint (e.g., '/cities', '/health')
 * @returns {string} Full API URL
 */
export const buildApiUrl = (endpoint) => {
  // Ensure endpoint starts with /api
  const normalizedEndpoint = endpoint.startsWith('/api') 
    ? endpoint 
    : `/api${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  
  // HARD FAILSAFE: Double-check in production
  if (isProduction() && API_BASE && containsForbiddenPattern(API_BASE)) {
    console.error('[API CONFIG] FAILSAFE TRIGGERED: Blocking external URL in production');
    return normalizedEndpoint; // Same-origin only
  }
  
  return `${API_BASE}${normalizedEndpoint}`;
};

/**
 * Health check function
 * @returns {Promise<{ok: boolean, data?: object, error?: string}>}
 */
export const checkApiHealth = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const url = buildApiUrl('/api/health');
    console.log('[API CONFIG] Health check:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('[API CONFIG] Health check passed:', data.status);
    return { ok: true, data };
  } catch (error) {
    console.error('[API CONFIG] Health check FAILED:', error.message);
    return { ok: false, error: error.message };
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Fetch wrapper with built-in protection
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const apiFetch = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  
  // Default headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  return fetch(url, { ...options, headers });
};

// ============================================================
// LIGHTWEIGHT AUTO-RECOVERY SYSTEM
// ============================================================
// - Max 2 retries with 300ms delay
// - Same-origin fallback if external URL fails
// - Clear error logging
// - Frontend cache for resilience
// ============================================================

const RETRY_CONFIG = {
  maxRetries: 2,
  delayMs: 300,
};

// ============================================================
// FRONTEND CACHE SYSTEM
// ============================================================
// - Cache last successful API responses
// - Use cached data if API fails
// - Refresh in background
// - Lightweight sessionStorage-based (cleared on tab close)
// ============================================================

const CACHE_PREFIX = 'api_cache_';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const cacheStore = {
  get: (key) => {
    try {
      const item = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!item) return null;
      
      const { data, timestamp } = JSON.parse(item);
      const isExpired = Date.now() - timestamp > CACHE_TTL_MS;
      
      // Return data even if expired (for fallback), but mark it
      return { data, isExpired, timestamp };
    } catch {
      return null;
    }
  },
  
  set: (key, data) => {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch {
      // Storage full or disabled - ignore
    }
  },
  
  clear: (key) => {
    try {
      sessionStorage.removeItem(CACHE_PREFIX + key);
    } catch {
      // Ignore
    }
  }
};

/**
 * Delay helper
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Safe API fetch with auto-recovery and caching
 * @param {string} endpoint - API endpoint (e.g., '/cities', '/health')
 * @param {object} options - Fetch options
 * @param {boolean} useCache - Whether to use caching (default: true for GET)
 * @returns {Promise<Response>}
 */
export const safeFetch = async (endpoint, options = {}, useCache = true) => {
  const normalizedEndpoint = endpoint.startsWith('/api') 
    ? endpoint 
    : `/api${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  // ── PLATFORM CONSENT INJECTION ─────────────────────────────────────────
  // Every public lead form submits here. Auto-merge the global consent state
  // (from <FormConsent />) + client metadata so ALL current & future forms
  // capture consent with zero per-page code. Also blocks submit when the
  // REQUIRED data-sharing consent is missing.
  if (normalizedEndpoint === '/api/leads/submit' && (options.method || '').toUpperCase() === 'POST') {
    try {
      const { getConsent, flagConsentError, PRIVACY_POLICY_VERSION } = await import('./consentStore');
      const consent = getConsent();
      if (!consent.dataSharing) {
        flagConsentError();
        return new Response(
          JSON.stringify({ detail: { error: 'consent_required', field: 'consent', message: 'You must accept data sharing to continue.' } }),
          { status: 400, headers: { 'Content-Type': 'application/json' } }
        );
      }
      let payload = {};
      try { payload = options.body ? JSON.parse(options.body) : {}; } catch { payload = null; }
      if (payload && typeof payload === 'object') {
        payload.consent_data_sharing = !!consent.dataSharing;
        payload.consent_marketing = !!consent.marketing;
        payload.privacy_policy_version = PRIVACY_POLICY_VERSION;
        if (!payload.referrer_url && typeof document !== 'undefined') {
          payload.referrer_url = document.referrer || '';
        }
        if (!payload.form_name) {
          payload.form_name = payload.landing_page_slug || 'lead_form';
        }
        options = { ...options, body: JSON.stringify(payload) };
      }
    } catch (e) {
      console.warn('[CONSENT] injection skipped:', e && e.message);
    }
  }
  // ───────────────────────────────────────────────────────────────────────

  
  // Do NOT force Content-Type when the body is a structured payload
  // (FormData / Blob / URLSearchParams / ArrayBuffer). The browser must set
  // the correct `multipart/form-data; boundary=...` header itself; if we
  // inject `application/json` here, multipart uploads fail with HTTP 422
  // because FastAPI cannot find the boundary.
  const body = options.body;
  const isStructuredBody = body && (
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
    (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer)
  );
  const headers = {
    ...(isStructuredBody ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers
  };
  
  const fetchOptions = { ...options, headers };
  const isGetRequest = !options.method || options.method === 'GET';
  // Fire the Google Ads lead conversion ONLY on a successful lead-form submit.
  const isLeadSubmit = normalizedEndpoint === '/api/leads/submit' && (options.method || '').toUpperCase() === 'POST';
  const cacheKey = normalizedEndpoint;
  
  // Primary URL (may be external in dev/preview)
  const primaryUrl = `${API_BASE}${normalizedEndpoint}`;
  // Fallback URL (always same-origin)
  const fallbackUrl = normalizedEndpoint;
  
  let lastError = null;
  
  // Try primary URL with retries
  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        await delay(RETRY_CONFIG.delayMs);
      }
      
      const response = await fetch(primaryUrl, fetchOptions);
      
      if (response.ok) {
        if (isLeadSubmit) trackLeadConversion();
        return response;
      }
      
      // Non-OK response - don't retry for client errors (4xx)
      if (response.status >= 400 && response.status < 500) {
        return response;
      }
      
      lastError = new Error(`HTTP ${response.status}`);
    } catch (error) {
      lastError = error;
      console.warn(`[API RETRY] Attempt ${attempt + 1} failed for ${normalizedEndpoint}:`, error.message);
    }
  }
  
  // If primary URL is external, try same-origin fallback
  if (API_BASE && API_BASE !== '') {
    console.warn(`[API FALLBACK] Trying same-origin for ${normalizedEndpoint}`);
    
    try {
      const response = await fetch(fallbackUrl, fetchOptions);
      if (response.ok) {
        console.log(`[API FALLBACK] Success with same-origin`);
        if (isLeadSubmit) trackLeadConversion();
        return response;
      }
    } catch (fallbackError) {
      console.error(`[API FALLBACK] Same-origin also failed:`, fallbackError.message);
    }
  }
  
  // All attempts failed - check cache for GET requests
  if (useCache && isGetRequest) {
    const cached = cacheStore.get(cacheKey);
    if (cached && cached.data) {
      console.warn(`[API CACHE] Using cached data for ${normalizedEndpoint} (age: ${Math.round((Date.now() - cached.timestamp) / 1000)}s)`);
      // Return a fake Response with cached data
      return new Response(JSON.stringify(cached.data), {
        status: 200,
        headers: { 'Content-Type': 'application/json', 'X-From-Cache': 'true' }
      });
    }
  }
  
  // All attempts failed
  console.error(`[API ERROR] ${normalizedEndpoint}:`, lastError?.message || 'Unknown error');
  throw lastError || new Error('API request failed');
};

/**
 * Safe JSON fetch with auto-recovery and caching
 * Returns parsed JSON or throws error
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<any>} Parsed JSON response
 */
export const safeJsonFetch = async (endpoint, options = {}) => {
  const normalizedEndpoint = endpoint.startsWith('/api') 
    ? endpoint 
    : `/api${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  
  const isGetRequest = !options.method || options.method === 'GET';
  const cacheKey = normalizedEndpoint;
  
  const response = await safeFetch(endpoint, options);
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error(`[API ERROR] ${endpoint}: HTTP ${response.status} - ${errorText}`);
    throw new Error(`HTTP ${response.status}`);
  }
  
  const data = await response.json();
  
  // Cache successful GET responses
  if (isGetRequest && data) {
    cacheStore.set(cacheKey, data);
  }
  
  return data;
};

// Export configuration
export const API_CONFIG = {
  BASE_URL: API_BASE,
  IS_PRODUCTION: isProduction(),
  SAME_ORIGIN: !API_BASE || API_BASE === '',
};

// Default export for convenience
export default {
  buildApiUrl,
  checkApiHealth,
  apiFetch,
  safeFetch,
  safeJsonFetch,
  API_CONFIG,
  API_BASE
};
